import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { Button, Card, HelpPanel, PageHeader } from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useMode } from '../../lib/useMode';

export function PaymentOnboardingPage() {
  // Mode is the app-wide Test/Live switch (lib/mode.ts), not a control local to this page - a
  // separate dropdown here used to let this screen's mode silently disagree with every other
  // screen's. The onboard call itself doesn't even need mode as a body field: `request()` already
  // sends it as the X-Loctopus-Mode header on every call, same as everywhere else in the app.
  const [mode] = useMode();
  const [error, setError] = useState<ParsedApiError | null>(null);

  const onboardMutation = useMutation({
    mutationFn: () =>
      api.paymentAccounts.onboard({
        provider: 'stripe',
        returnUrl: `${window.location.origin}/payment-accounts?onboarding=return`,
        refreshUrl: `${window.location.origin}/payment-accounts?onboarding=refresh`,
      }),
    onSuccess: (res) => {
      window.location.href = res.onboardingUrl;
    },
    onError: (err) => setError(toParsedError(err)),
  });

  return (
    <div>
      <PageHeader
        title="Payment setup"
        description="Connect a Stripe account so Loctopus can settle your sales."
      />
      <HelpPanel storageKey="help-payment-setup" title="What is this?">
        This connects a Stripe account so buyer payments can reach you. While you're in Test mode
        (the switch in the sidebar), this sets up a TEST Stripe account using fake money - no real
        charges happen until you switch to Live and connect a real one.
      </HelpPanel>
      {error && <ErrorSummary error={error} className="mb-3" />}
      <Card title="Stripe Connect">
        <p className="text-sm text-slate-600 mb-4">
          Clicking below calls{' '}
          <code className="font-mono text-xs">POST /dashboard/payment-accounts/onboard</code> and
          redirects your browser to the hosted Stripe onboarding flow it returns, in{' '}
          <strong>{mode === 'test' ? 'Test' : 'Live'}</strong> mode (switch Test/Live in the
          sidebar to connect a different mode's account).
        </p>
        <RequirePermission
          permission="payment_accounts.manage"
          fallback={
            <p className="text-sm text-slate-500">
              You don't have permission to connect a payment account.
            </p>
          }
        >
          <Button onClick={() => onboardMutation.mutate()} disabled={onboardMutation.isPending}>
            {onboardMutation.isPending ? 'Starting onboarding...' : 'Connect Stripe account'}
          </Button>
        </RequirePermission>
      </Card>
    </div>
  );
}
